import { SAP_L10N_MAP } from '#config/routes-l10n';

import type { EmptyPrefetchProps } from '#src/shared/prefetch';
import { withPrefetchedData } from '#src/shared/prefetch';

import { EntryPoint } from './EntryPoint';

/**
 * Resolves the locale and slug to a URL path for the start a petition intro page.
 * The URL path is in the format of /{locale}/{slug}/intro
 * where locale is in the format of xx or xx-XX (e.g. en or en-US) and slug (e.g. start-a-petition or iniciar-una-petición) is URL encoded.
 */
function resolveLocale(locale: string, slug: string) {
	return locale.replace(
		/^([a-z]{2})(-[a-z]{2})?$/i,
		(_, g1: string, g2: string) =>
			`/${g1.toLowerCase()}${g2 ? g2.toUpperCase() : ''}/${encodeURIComponent(slug)}/intro`,
	);
}

/**
 * We're separating the data fetching from the page meta due to page data relying on localstorage.
 * This allows us to fetch the data on the client side, while still being able to set the page meta on the server side.
 * Once moving draft data to the server, we can collapse this back into one prefetched component.
 */
export const StartAPetition = withPrefetchedData(
	function StartAPetition(_props: EmptyPrefetchProps) {
		return <EntryPoint />;
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchData: async () => {},
		pageTitle: ({ i18n, fromTemplate }) => fromTemplate(i18n.translate('corgi.start_a_petition.meta_tags.title')),
		pageMetadata: ({ params, i18n }) => {
			const robots = params.step !== 'intro' ? 'noindex, nofollow' : undefined;
			return {
				description: i18n.translate('corgi.start_a_petition.meta_tags.description'),
				robots,
			};
		},
		pageCanonical: ({ params, pathname }) => {
			if (params.step !== 'intro') return undefined;

			const locale = params.locale || pathname.split('/')[1];
			if (locale) {
				const validL10n = SAP_L10N_MAP[locale.toLowerCase()];
				if (validL10n) {
					return resolveLocale(locale, validL10n.sap);
				}
			}
			return '/start-a-petition/intro';
		},
	},
);
